import {useMemo, useState} from 'react'
import {Button, Spinner, Table, Modal, Form, Badge} from 'react-bootstrap'
import {useQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {PERMISSION_CATALOG, ROLES, CREATE_ROLE, UPDATE_ROLE, DELETE_ROLE} from '../../../../gql/authz'

type Role = {id: string; name: string; member_count: number; permissions: string[]}

// Roles management: a clear list of roles with member/permission counts and
// explicit Create / Edit permissions / Rename / Delete actions.
export const RolesManager = ({onEditRole}: {onEditRole: (id: string) => void}) => {
  const {data, loading, refetch} = useQuery(ROLES, {fetchPolicy: 'cache-and-network'})
  const {data: catData} = useQuery(PERMISSION_CATALOG, {fetchPolicy: 'cache-first'})
  const [createRole, {loading: creating}] = useMutation(CREATE_ROLE)
  const [updateRole, {loading: renaming}] = useMutation(UPDATE_ROLE)
  const [deleteRole] = useMutation(DELETE_ROLE)

  const roles: Role[] = useMemo(() => data?.roles ?? [], [data])

  // Catalog codenames + total, to show "X / Y permissions" (ignoring the noisy
  // built-in Django perms that admins accumulate).
  const catalog = useMemo(() => {
    const set = new Set<string>()
    let total = 0
    ;(catData?.permissionCatalog?.modules ?? []).forEach((m: any) =>
      m.permissions.forEach((p: any) => {
        set.add(p.codename)
        total += 1
      })
    )
    return {set, total}
  }, [catData])

  const [modal, setModal] = useState<{mode: 'create' | 'rename'; id?: string; name: string} | null>(null)

  const submitModal = async () => {
    const name = modal?.name.trim()
    if (!name) return
    try {
      if (modal!.mode === 'create') {
        const {data: r} = await createRole({variables: {name}})
        if (r?.createRole?.success) toast.success(`Role "${name}" created.`)
      } else {
        const {data: r} = await updateRole({variables: {roleId: modal!.id, name}})
        if (r?.updateRole?.success) toast.success('Role renamed.')
      }
      setModal(null)
      await refetch()
    } catch (err: any) {
      toast.error(err?.message || 'Could not save the role')
    }
  }

  const handleDelete = async (r: Role) => {
    if (r.member_count > 0) {
      toast.warning(`"${r.name}" has ${r.member_count} member(s). Reassign them first.`)
      return
    }
    if (!window.confirm(`Delete role "${r.name}"? This cannot be undone.`)) return
    try {
      const {data: res} = await deleteRole({variables: {roleId: r.id}})
      if (res?.deleteRole?.success) {
        toast.success(`Role "${r.name}" deleted.`)
        await refetch()
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete the role')
    }
  }

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <span className='text-muted fs-7'>{roles.length} role(s)</span>
        <Button variant='primary' size='sm' onClick={() => setModal({mode: 'create', name: ''})}>
          <i className='bi bi-plus fs-5' /> New role
        </Button>
      </div>

      {loading && !roles.length ? (
        <div className='py-10 text-center'><Spinner animation='border' /></div>
      ) : (
        <Table hover responsive className='align-middle'>
          <thead className='table-light'>
            <tr className='fw-bold text-muted'>
              <th className='ps-4'>Role</th>
              <th className='text-center'>Members</th>
              <th className='text-center'>Permissions</th>
              <th className='text-end pe-4'>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => {
              const permCount = r.permissions.filter((c) => catalog.set.has(c)).length
              return (
                <tr key={r.id}>
                  <td className='ps-4 fw-bold'>{r.name}</td>
                  <td className='text-center'>
                    <Badge bg='light' text='dark'>{r.member_count}</Badge>
                  </td>
                  <td className='text-center'>
                    <span className={`badge ${permCount === 0 ? 'badge-light' : permCount === catalog.total ? 'badge-light-success' : 'badge-light-primary'}`}>
                      {permCount}{catalog.total ? ` / ${catalog.total}` : ''}
                    </span>
                  </td>
                  <td className='text-end pe-4'>
                    <div className='d-flex justify-content-end gap-2'>
                      <Button variant='light-primary' size='sm' onClick={() => onEditRole(r.id)}>
                        <i className='bi bi-sliders me-1' /> Edit permissions
                      </Button>
                      <Button
                        variant='light'
                        size='sm'
                        title='Rename'
                        onClick={() => setModal({mode: 'rename', id: r.id, name: r.name})}
                      >
                        <i className='bi bi-pencil' />
                      </Button>
                      <Button
                        variant='light-danger'
                        size='sm'
                        title={r.member_count ? 'Reassign members before deleting' : 'Delete'}
                        onClick={() => handleDelete(r)}
                      >
                        <i className='bi bi-trash' />
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
            {roles.length === 0 && (
              <tr>
                <td colSpan={4} className='text-center text-muted py-6'>No roles yet. Create one to start.</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={!!modal} onHide={() => setModal(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modal?.mode === 'create' ? 'New role' : 'Rename role'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label className='small text-muted'>Role name</Form.Label>
          <Form.Control
            autoFocus
            value={modal?.name ?? ''}
            onChange={(e) => setModal((m) => (m ? {...m, name: e.target.value} : m))}
            onKeyDown={(e) => e.key === 'Enter' && submitModal()}
            placeholder='e.g. Supervisor de Ventas'
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='light' onClick={() => setModal(null)}>Cancel</Button>
          <Button variant='primary' onClick={submitModal} disabled={creating || renaming || !modal?.name.trim()}>
            {creating || renaming ? <Spinner animation='border' size='sm' /> : modal?.mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
